import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { IonInfiniteScroll, ModalController } from '@ionic/angular';
import { FavoriteExerciseRequest, GetExerciseFavoritesResponse } from '@me-fit-mono/typings';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { ME_Fit } from 'me-fit-typings';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, from, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, mergeMap, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { fadeInOut, slideUpDown } from 'src/app/animations';
import { Feedback } from 'src/app/decorators/feedback-decorator';
import { AlgoliaService } from 'src/app/services/algolia.service';
import { AuthService } from 'src/app/services/auth.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { EmployeesStateService } from 'src/app/state/employees.state.service';
import { ExerciseStateService } from 'src/app/state/exercise.state.service';
import { OrganizationStateService } from 'src/app/state/organization.state.service';

interface ExerciseFilterState {
  name: string;
  page: number;
  selectedFacets: Partial<Record<keyof ME_Fit.Exercise, Set<string>>>;
  selectedFavoritesIds: Set<string>;
  filterByOrganization: boolean;
}

type FavoriteOptionType = 'employee' | 'organization' | 'organizationExercises';

interface FavoriteOption {
  i18nKey: string;
  i18nParams?: Record<string, string>;
  type: FavoriteOptionType;
  totalsForCurrentQuery: number;
  disabled: boolean;
}

@Component({
  selector: 'app-exercise-filter-modal',
  templateUrl: './exercise-filter-modal.component.html',
  styleUrls: ['./exercise-filter-modal.component.scss'],
  animations: [slideUpDown, fadeInOut]
})
export class ExerciseFilterModalComponent implements OnInit, OnDestroy {

  @Input() anonymous = false;

  defaultExerciseFilteredRequestState: ExerciseFilterState = {
    page: 0,
    name: '',
    selectedFacets: { },
    selectedFavoritesIds: new Set(),
    filterByOrganization: false
  };

  exerciseFilteredRequestState$ = new BehaviorSubject<ExerciseFilterState>(this.defaultExerciseFilteredRequestState);

  searchInputCtrl = new FormControl<string>('', {nonNullable: true })

  exerciseSearchQueryResult$ = this.exerciseFilteredRequestState$.pipe(
    switchMap((filterState) => {

      const [query, requestOptions] = this.getSearchIndexParams(filterState);

      const favoritesFilter = filterState.selectedFavoritesIds.size > 0
        ? this.$algolia.getObjectIdsFilter([...filterState.selectedFavoritesIds])
        : '';

      if (this.anonymous) {
        return from(this.$algolia.searchAnonymousIndex<ME_Fit.Exercise>(query, {
          ...requestOptions,
          filters: favoritesFilter
        }));
      }


      if (filterState.filterByOrganization) {
        // The scoped index is already restricted to this org's exercises via the API key.
        // We pass the favorites objectID filter (if any) on top of that restriction.
        return from(this.$algolia.searchScopedIndex<ME_Fit.Exercise>(query, {
          ...requestOptions,
          filters: favoritesFilter
        }));
      }

      return from(this.$algolia.searchGlobalIndex<ME_Fit.Exercise>(query, {
        ...requestOptions,
        filters: favoritesFilter
      }));
    }),
    shareReplay(1)
  );

  exerciseFacets$ = this.exerciseSearchQueryResult$.pipe(
    map( response => response.facets),
    distinctUntilChanged((prev, next) => isEqual(prev, next)),
  );

  disableInfiniteScroll$ = combineLatest([
    this.exerciseSearchQueryResult$,
    this.exerciseFilteredRequestState$
  ]).pipe(
    map(([queryResult, requestState]) => {
      const isLastPage = requestState.page === queryResult.nbPages;

      return isLastPage;
    })
  )

  /** this is dead code but might be useful in the future */
  isLoading$ = new BehaviorSubject(false);

  exercises$ = new BehaviorSubject<ME_Fit.Exercise[]>([]);

  total$ = this.exerciseSearchQueryResult$.pipe(
    map(response => response.nbHits)
  );

  noExercisesFound$ = combineLatest([
    this.total$,
    this.isLoading$
  ]).pipe(
    map(([total, isLoading]) => total === 0 && !isLoading)
  );

  @ViewChild(IonInfiniteScroll) ionInfiniteScroll: IonInfiniteScroll;

  selectedExercises = new Set<ME_Fit.Exercise>();

  defaultFilterState = {
    region: true,
    equipment: true,
  }

  filtersOpenState = this.defaultFilterState;

  get selectedFacets(): ExerciseFilterState['selectedFacets'] {
    return this.exerciseFilteredRequestState$.value.selectedFacets;
  }

  moreDetailsExercise: ME_Fit.Exercise | null = null;

  moreDetailsExerciseFavoriteForm = this.fb.nonNullable.group({
    isOrganizationFavorite: [false],
    isEmployeeFavorite: [false]
  });

  headerText$: Observable<string> = combineLatest([
    this.total$,
    this.isLoading$
  ]).pipe(
    mergeMap(([total, isLoading]) => {
      if (isLoading) {
        return this.$translate.get('exercise.filter.modal.exercise.loading');
      } else if (total === 0) {
        return this.$translate.get('exercise.filter.modal.no.exercises');
      } else if (total > 1) {
        return this.$translate.get('exercise.filter.modal.total.exercise.plural', { total });
      } else if (total === 1) {
        return this.$translate.get('exercise.filter.modal.total.exercise.singular');
      }

      return of('');
    })
  );

  private stop$ = new Subject<void>();

  /**
   * Total favorites for current query and facets
   */
  favoriteTotals$ = combineLatest([
    this.exerciseFilteredRequestState$,
    this.$employee.getExerciseFavorites.getResponse$(),
    this.$organization.getExerciseFavorites.getResponse$(),
  ]).pipe(
    mergeMap(async ([filterState, employeeFavorites, organizationFavorites]) => {
      const employeeFavoritesTotal = await this.getTotalsForFavorites(employeeFavorites, filterState);
      const organizationFavoritesTotal = await this.getTotalsForFavorites(organizationFavorites, filterState);
      const organizationExercisesTotal = await this.getTotalsForOrganization(filterState);

      return {
        employee: employeeFavoritesTotal,
        organization: organizationFavoritesTotal,
        organizationExercises: organizationExercisesTotal
      } as const;
    })
  );

  organizationName$ = this.$auth.organization$.pipe(
    map(org => org?.name ?? ''),
    startWith('')
  );

  defaultFavoriteSectionOpen = true;

  favoritesSectionOpen = this.defaultFavoriteSectionOpen;

  favoriteRadioControl = new FormControl<FavoriteOptionType | null>(null);

  favoriteOptions$: Observable<FavoriteOption[]> = combineLatest([
    this.favoriteTotals$,
    this.organizationName$
  ]).pipe(
    map(([favoriteTotals, organizationName]) => {
    return [{
        i18nKey: 'exercise.favorites.employee',
        type: 'employee',
        totalsForCurrentQuery: favoriteTotals.employee,
        disabled: favoriteTotals.employee === 0
      }, {
        i18nKey: 'exercise.favorites.organization',
        type: 'organization',
        totalsForCurrentQuery: favoriteTotals.organization,
        disabled: favoriteTotals.organization === 0
      }, {
        i18nKey: 'exercise.library.organization.exercises',
        i18nParams: { orgName: organizationName },
        type: 'organizationExercises',
        totalsForCurrentQuery: favoriteTotals.organizationExercises,
        disabled: favoriteTotals.organizationExercises === 0
      }] satisfies FavoriteOption[];
    })
  );

  public get totalSelectedfavoriteOptions(): number {
    return this.favoriteRadioControl.value ? 1 : 0;
  };

  videoPreviewEnabledId: string|null;

  videoPreviewLoading: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private $translate: TranslateService,
    private $algolia: AlgoliaService,
    private $employee: EmployeesStateService,
    private $organization: OrganizationStateService,
    private fb: FormBuilder,
    private $exerciseState: ExerciseStateService,
    private $breakpoints: BreakpointsService,
    private $auth: AuthService
  ) { }

  ngOnInit() {
    this.initBreakpointListener();

    this.exerciseSearchQueryResult$.subscribe( exercisesFilteredResponse => {

      // If the first page is being fetched, we will clear the list
      if (exercisesFilteredResponse.page === 0) {
        this.exercises$.next(exercisesFilteredResponse.hits);
      } else {
        // Otherwise we will merge the current list with the new one
        //
        this.exercises$.next([...this.exercises$.value, ...exercisesFilteredResponse.hits]);
      }
    });

    this.searchInputCtrl.valueChanges.pipe(
      takeUntil(this.stop$),
      debounceTime(300),
    ).subscribe((newSearchValueChange) => {
      this.exerciseFilteredRequestState$.next({
        ...this.defaultExerciseFilteredRequestState,
        ...this.exerciseFilteredRequestState$.value,
        name: newSearchValueChange,
        page: 0
      });
    });


    if (this.anonymous) {
      return;
    }

    // If the selected radio option has 0 results, clear the selection
    this.favoriteTotals$.pipe(
      takeUntil(this.stop$),
    ).subscribe((totals) => {
      const selected = this.favoriteRadioControl.value;
      if (
        (selected === 'employee' && totals.employee === 0) ||
        (selected === 'organization' && totals.organization === 0) ||
        (selected === 'organizationExercises' && totals.organizationExercises === 0)
      ) {
        this.favoriteRadioControl.setValue(null);
      }
    });

    // When the radio selection changes, update the filter state.
    // Always reset page to 0 so the exercise list is cleared for the new filter.
    combineLatest([
      this.favoriteRadioControl.valueChanges.pipe(startWith(this.favoriteRadioControl.value)),
      // Including favorites here so we re-trigger search if they change while a filter is active
      this.$employee.getExerciseFavorites.getResponse$(),
      this.$organization.getExerciseFavorites.getResponse$(),
    ]).pipe(
      takeUntil(this.stop$),
    ).subscribe(([selectedType, employeeFavorites, organizationFavorites]) => {
      const selectedFavoritesIds = new Set<string>();

      if (selectedType === 'employee') {
        (employeeFavorites?.favoriteExerciseIds ?? []).forEach(id => selectedFavoritesIds.add(id));
      } else if (selectedType === 'organization') {
        (organizationFavorites?.favoriteExerciseIds ?? []).forEach(id => selectedFavoritesIds.add(id));
      }

      this.exerciseFilteredRequestState$.next({
        ...this.exerciseFilteredRequestState$.value,
        selectedFavoritesIds,
        filterByOrganization: selectedType === 'organizationExercises',
        page: 0,
      });
    });

    this.moreDetailsExerciseFavoriteForm.controls.isEmployeeFavorite.valueChanges.pipe(
      takeUntil(this.stop$),
      // We only want to react to this if we have "more details" exercise
      filter(() => !!this.moreDetailsExercise)
    ).subscribe((newIsFavoriteValue) => {
      this.updateExerciseFavorite('employee', newIsFavoriteValue);
    });

    this.moreDetailsExerciseFavoriteForm.controls.isOrganizationFavorite.valueChanges.pipe(
      takeUntil(this.stop$),
      // We only want to react to this if we have "more details" exercise
      filter(() => !!this.moreDetailsExercise)
    ).subscribe((newIsFavoriteValue) => {
      this.updateExerciseFavorite('organization', newIsFavoriteValue);
    });
  }

  private initBreakpointListener() {
    this.$breakpoints.getLargeBreakpointObserver()
      .pipe(takeUntil(this.stop$))
      .subscribe(result => {
        const aboveThanLargeBreakpointMatch = result.matches;

        if (aboveThanLargeBreakpointMatch) {
          this.filtersOpenState = this.defaultFilterState;
          this.favoritesSectionOpen = this.defaultFavoriteSectionOpen;
        } else {
          // Hide everything on mobile
          this.filtersOpenState = Object.entries(this.defaultFilterState).reduce((acc, [key, _]) => {
            acc[key] = false;
            return acc;
          }, {} as typeof this.filtersOpenState);
          this.favoritesSectionOpen = false;
        }
      });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async loadData(event) {
    const newPageValue = this.exerciseFilteredRequestState$.value.page! + 1;

    console.log('[ExerciseFilterModalComponent:loadData] loading more exercises', newPageValue);

    this.exerciseFilteredRequestState$.next({
      ...this.exerciseFilteredRequestState$.value,
      page: newPageValue,
    });

    await this.exerciseSearchQueryResult$.pipe(
      first( response => response.page == newPageValue)
    ).toPromise();

    // Remove the loader
    //
    this.ionInfiniteScroll.complete();
  }

  selectExercise(exercise: ME_Fit.Exercise) {
    this.selectedExercises.add(exercise);
  }

  removeExerciseFromSelection(exercise: ME_Fit.Exercise) {
    this.selectedExercises.delete(exercise);
  }

  resetFilters() {
    const resetSelectedFacets: Partial<Record<keyof ME_Fit.Exercise, Set<string>>> = {};
    Object.keys(this.exerciseFilteredRequestState$.value.selectedFacets).forEach((key) => {
      resetSelectedFacets[key] = new Set();
    });

    this.searchInputCtrl.reset('');
    this.favoriteRadioControl.setValue(null);

    this.exerciseFilteredRequestState$.next({
      ...this.defaultExerciseFilteredRequestState,
      selectedFacets: resetSelectedFacets
    });

    this.ionInfiniteScroll.disabled = false;
  }

  onFilterStateChange(newSelectedOptions: Set<string>, fieldName: string) {
    console.log(`[ExerciseFilterModalComponent:onFilterStateChange] ${fieldName} ${newSelectedOptions}`);

    const newSelectedFacets = this.exerciseFilteredRequestState$.value.selectedFacets;

    newSelectedFacets[fieldName] = newSelectedOptions;

    this.exerciseFilteredRequestState$.next({
      ...this.defaultExerciseFilteredRequestState,
      ...this.exerciseFilteredRequestState$.value,
      page: 0,
      selectedFacets: newSelectedFacets
    });

    this.ionInfiniteScroll.disabled = false;
  }

  async setMoreDetailsExercise(exercise: ME_Fit.Exercise|null) {
    this.moreDetailsExercise = exercise;

    if (this.anonymous) {
      return;
    }

    // Setting favorite form state
    if (exercise) {
      const employeeFavorites = await firstValueFrom(this.$employee.getExerciseFavorites.getResponse$().pipe(first()));
      const organizationFavorites = await firstValueFrom(this.$organization.getExerciseFavorites.getResponse$().pipe(first()));
      this.moreDetailsExerciseFavoriteForm.setValue({
        isEmployeeFavorite: (employeeFavorites?.favoriteExerciseIds ?? []).includes(exercise.id) ?? false,
        isOrganizationFavorite: (organizationFavorites?.favoriteExerciseIds ?? []).includes(exercise.id) ?? false
      }, {
        emitEvent: false
      });
    } else {
      this.moreDetailsExerciseFavoriteForm.reset({}, {emitEvent: false});
    }
  }

  addExercises() {
    this.modalCtrl.dismiss([...this.selectedExercises])
  }

  ngOnDestroy() {
    this.stop$.next();
  }

  toggleFavoritesOpenState() {
    this.favoritesSectionOpen = !this.favoritesSectionOpen;
  }

  private getSearchIndexParams(filterState: ExerciseFilterState): Parameters<AlgoliaService['searchGlobalIndex']> {

    const facetFilters: string[] = [];

    Object.entries(filterState.selectedFacets).forEach(([fieldName, selectedValues]) => {
      selectedValues.forEach(selectedValue => {
        facetFilters.push(`${fieldName}:${selectedValue}`);
      })
    });

    return [filterState.name, {
      facets: ['*'],
      facetFilters,
      page: filterState.page,
      query: filterState.name
    }]
  }

  /**
   * Calls our search index and gets the total of the favorites for the current query, that way we emulate
   * facets for the favorites
   */
  async getTotalsForFavorites(favoriteResponse: GetExerciseFavoritesResponse|undefined, filterState: ExerciseFilterState): Promise<number> {
    const exerciseIds = favoriteResponse?.favoriteExerciseIds ?? [];

    if (exerciseIds.length === 0) {
      return 0;
    }

    const [query, requestOptions] = this.getSearchIndexParams(filterState);

    const filtersForFavorites = exerciseIds.length > 0 ? this.$algolia.getObjectIdsFilter(exerciseIds) : undefined;

    const {nbHits} = await this.$algolia.searchGlobalIndex<ME_Fit.Exercise>(query, {
      ...requestOptions,
      filters: filtersForFavorites
    });

    return nbHits;
  }

  /**
   * Uses the scoped index (current org only, enforced by the API key) to count
   * exercises uploaded by the organization for the current query and facets.
   */
  async getTotalsForOrganization(filterState: ExerciseFilterState): Promise<number> {
    const [query, requestOptions] = this.getSearchIndexParams(filterState);

    const { nbHits } = await this.$algolia.searchScopedIndex<ME_Fit.Exercise>(query, requestOptions);

    return nbHits;
  }

  @Feedback({
    i18nSuccessKey: 'exercise.favorite.update.success',
    i18nFailKey: 'exercise.favorite.update.fail'
  })
  updateExerciseFavorite(favoriteType: FavoriteExerciseRequest['favoriteType'], isFavorite: boolean) {
    return this.$exerciseState.favoriteExercise.call({
      pathParts: {
        id: this.moreDetailsExercise!.id
      },
      request: {
        favoriteType,
        isFavorite
      }
    })
  }

  onVideoPreviewLoading(isLoading: boolean) {
    this.videoPreviewLoading = isLoading;
  }
}
